import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAmbientFrogScheduler } from './useAmbientFrogScheduler';
import './AmbientFrogs.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculateAnchorPosition = (scene) => {
  const anchor = document.querySelector(scene.anchorSelector);
  if (!anchor) {
    return null;
  }

  const rect = anchor.getBoundingClientRect();
  const width = Math.min(scene.width, window.innerWidth - 32);
  const imageHeight = width / scene.imageAspectRatio;
  let left;
  let top;

  if (scene.placement === 'top-center') {
    const visibleHeight = imageHeight * 0.9;
    left = rect.left + (rect.width - width) / 2;
    top = rect.top - visibleHeight;

    return {
      height: `${visibleHeight}px`,
      left: `${clamp(left, 16, window.innerWidth - width - 16)}px`,
      top: `${top}px`,
      width: `${width}px`,
    };
  } else {
    left = scene.placement === 'bottom-left'
      ? rect.left - width * 0.22
      : rect.right - width * 0.78;
    top = rect.bottom - width * 0.34;
  }

  return {
    left: `${clamp(left, 16, window.innerWidth - width - 16)}px`,
    top: `${top}px`,
    width: `${width}px`,
  };
};

const useAnchorPosition = (scene) => {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!scene?.anchorSelector) {
      setPosition(null);
      return undefined;
    }

    let frameId;
    const updatePosition = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        setPosition(calculateAnchorPosition(scene));
      });
    };

    setPosition(calculateAnchorPosition(scene));
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [scene]);

  return position;
};

export const AmbientFrogs = () => {
  const activeScene = useAmbientFrogScheduler();
  const anchorPosition = useAnchorPosition(activeScene);

  if (!activeScene || (activeScene.anchorSelector && !anchorPosition)) {
    return null;
  }

  const actorStyle = {
    ...anchorPosition,
    '--frog-image-aspect': activeScene.imageAspectRatio,
  };

  return createPortal(
    <div
      key={activeScene.id}
      className={`ambient-frog ambient-frog--${activeScene.animation}`}
      style={actorStyle}
      aria-hidden="true"
      data-frog-scene={activeScene.id}
    >
      <div className="ambient-frog__motion">
        <div className="ambient-frog__art-window">
          <img
            className="ambient-frog__art"
            src={activeScene.image}
            alt=""
            draggable="false"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
